import React from "react";
import { cn } from "../utils/cn";

/**
 * DashboardSkeleton — High-fidelity shimmer loading skeleton.
 *
 * Mirrors the actual dashboard layout: header with avatar, scenario selector,
 * hero card with text lines, summary cards grid, stats + readiness gauge,
 * and chart area. Uses differentiated shading for visual hierarchy.
 */
export const DashboardSkeleton: React.FC = React.memo(() => {
  return (
    <div
      className="dashboard-skeleton skeleton-fade-in"
      id="dashboard-skeleton"
    >
      {/* Header bar with avatar circle */}
      <div className={cn("skeleton-header", "skeleton-shimmer")}>
        <div className="skeleton-header-avatar" />
      </div>

      {/* Scenario selector pill — left-aligned */}
      <div className={cn("skeleton-scenario", "skeleton-shimmer")} />

      {/* Hero card — taller, darker shade */}
      <div className={cn("skeleton-hero", "skeleton-shimmer")}>
        <div className="flex flex-col gap-3 p-6">
          <div
            className={cn("skeleton-text-line", "skeleton-text-line--short")}
          />
          <div
            className={cn("skeleton-text-line", "skeleton-text-line--medium")}
          />
          <div
            className={cn("skeleton-text-line", "skeleton-text-line--long")}
          />
        </div>
      </div>

      {/* Summary cards grid — 4 columns on desktop, 2 on mobile */}
      <div className="skeleton-cards-grid">
        <div className={cn("skeleton-summary-card", "skeleton-shimmer")} />
        <div className={cn("skeleton-summary-card", "skeleton-shimmer")} />
        <div className={cn("skeleton-summary-card", "skeleton-shimmer")} />
        <div className={cn("skeleton-summary-card", "skeleton-shimmer")} />
      </div>

      {/* Stats + readiness gauge layout */}
      <div className="skeleton-stats-layout">
        <div className="skeleton-stats-grid">
          <div className={cn("skeleton-stat-card", "skeleton-shimmer")} />
          <div className={cn("skeleton-stat-card", "skeleton-shimmer")} />
          <div className={cn("skeleton-stat-card", "skeleton-shimmer")} />
          <div className={cn("skeleton-stat-card", "skeleton-shimmer")} />
        </div>
        <div className={cn("skeleton-readiness", "skeleton-shimmer")}>
          <div className="skeleton-gauge-circle" />
        </div>
      </div>

      {/* Chart area */}
      <div className={cn("skeleton-chart", "skeleton-shimmer")} />

      {/* Loading text */}
      <p className="skeleton-loading-text">Loading your retirement plan...</p>
    </div>
  );
});

DashboardSkeleton.displayName = "DashboardSkeleton";
